<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type Client, clientsApi } from '@/api/entities/review/clients'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'

definePage({
  meta: {
    title: '자동 이관 관리',
    action: 'read',
    subject: 'review',
  },
})

// 로컬 인터페이스
interface DriverConfigRow {
  id: number
  driver: string
  product_count: number
  monthly_state: boolean
  start: string | null
  end: string | null
}

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const clients = ref<Client[]>([])
const driverConfigs = ref<DriverConfigRow[]>([])
const totalActiveClients = ref(0)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 테이블 헤더
const clientHeaders = [
  { title: '이름', key: 'name', sortable: true },
  { title: '타입', key: 'type', sortable: true },
  { title: '정기 이관', key: 'has_monthly', sortable: false },
]

const driverConfigHeaders = [
  { title: 'ID', key: 'id', sortable: true },
  { title: '채널', key: 'driver', sortable: true },
  { title: '매핑 제품 수', key: 'product_count', sortable: true },
  { title: '정기 이관', key: 'monthly_state', sortable: false },
  { title: '시작일', key: 'start', sortable: false },
  { title: '종료일', key: 'end', sortable: false },
  { title: '', key: 'actions', sortable: false, width: '100px' },
]

// 데이터 로드
const loadClients = async () => {
  try {
    const { success, data } = await clientsApi.listClients({
      per_page: 1000,
      type: 'migration',
    })

    if (success && data) {
      clients.value = data.data || []

      // 정기 이관 활성화된 클라이언트 수 계산
      totalActiveClients.value = clients.value.filter(c => c.has_monthly_config).length
    }
  }
  catch (error) {
    console.error('Failed to load clients:', error)
  }
}

const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  driverConfigs.value = []

  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 100,
    })

    if (success && data) {
      driverConfigs.value = (data.data || []).map((config: DriverConfig) => ({
        id: config.id,
        driver: config.download_driver?.name || `드라이버 ${config.id}`,
        product_count: config.target_item_maps_count || 0,
        monthly_state: config.monthly_state === 'Y',
        start: config.auto_migration_start_date || null,
        end: config.auto_migration_end_date || null,
      }))
    }
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
  }
  finally {
    loading.value = false
  }
}

// 저장 가능 여부 확인
const ableToSave = (item: DriverConfigRow) => {
  if (item.monthly_state)
    return item.start && item.end

  return true
}

// 설정 저장
const save = async (item: DriverConfigRow) => {
  if (!selectedClient.value)
    return

  const payload = {
    monthly_state: item.monthly_state ? 'Y' : 'N',
    auto_migration_start_date: item.start,
    auto_migration_end_date: item.end,
  }

  try {
    const { success } = await driverConfigsApi.updateDriverConfig(
      selectedClient.value.id,
      item.id,
      payload,
    )

    if (success) {
      showNotification('정기 이관 설정 저장에 성공했습니다.')
      await loadClients()
    }
    else {
      showNotification('정기 이관 설정 저장에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to save driver config:', error)
    showNotification('정기 이관 설정 저장에 실패했습니다.', 'error')
  }
}

// 행 스타일
const markingConfig = (item: DriverConfigRow) => {
  if (item.monthly_state)
    return 'bg-success-lighten'

  return ''
}

// 클라이언트 선택
const selectClient = (client: Client) => {
  clientStore.updateSelectedClient(client)
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 감시자
watch(selectedClient, () => {
  if (selectedClient.value)
    loadDriverConfigs()
  else
    driverConfigs.value = []
})

onMounted(() => {
  loadClients()
  if (selectedClient.value)
    loadDriverConfigs()
})
</script>

<template>
  <VContainer fluid>
    <VRow>
      <!-- 클라이언트 목록 -->
      <VCol
        cols="12"
        xl="3"
      >
        <VCard>
          <VCardTitle>고객사 목록</VCardTitle>
          <VCardText>
            <VDataTable
              :headers="clientHeaders"
              :items="clients"
              :items-per-page="20"
              density="compact"
              class="elevation-0"
              @click:row="(_, { item }) => selectClient(item)"
            >
              <template #item.name="{ item }">
                <span :class="{ 'font-weight-bold text-primary': selectedClient?.id === item.id }">
                  {{ item.name }}
                </span>
              </template>
              <template #item.has_monthly="{ item }">
                <VIcon
                  v-if="item.has_monthly_config"
                  icon="tabler-check"
                  color="success"
                />
              </template>
            </VDataTable>
          </VCardText>
        </VCard>
      </VCol>

      <!-- 메인 콘텐츠 -->
      <VCol
        cols="12"
        xl="9"
      >
        <!-- 통계 카드 -->
        <VRow class="mb-4">
          <VCol
            cols="12"
            md="6"
          >
            <VCard>
              <VCardText class="d-flex align-center justify-space-between">
                <div>
                  <p class="text-subtitle-2 mb-1">
                    정기 이관 활성 고객사
                  </p>
                  <h4 class="text-h4">
                    {{ totalActiveClients }}
                  </h4>
                </div>
                <VAvatar
                  color="success"
                  variant="tonal"
                  rounded
                >
                  <VIcon icon="tabler-users" />
                </VAvatar>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>

        <!-- 채널 목록 -->
        <VCard v-if="selectedClient && selectedClient.type === 'migration'">
          <VCardTitle>채널 목록 ({{ selectedClient.name }})</VCardTitle>
          <VCardText>
            <VDataTable
              :headers="driverConfigHeaders"
              :items="driverConfigs"
              :loading="loading"
              class="elevation-0"
              :row-props="({ item }) => ({ class: markingConfig(item) })"
            >
              <!-- 정기 이관 -->
              <template #item.monthly_state="{ item }">
                <VSwitch
                  v-model="item.monthly_state"
                  hide-details
                  color="success"
                />
              </template>

              <!-- 시작일 -->
              <template #item.start="{ item }">
                <VTextField
                  v-model="item.start"
                  type="date"
                  density="compact"
                  hide-details
                  :disabled="!item.monthly_state"
                  variant="outlined"
                  style="min-width: 150px"
                />
              </template>

              <!-- 종료일 -->
              <template #item.end="{ item }">
                <VTextField
                  v-model="item.end"
                  type="date"
                  density="compact"
                  hide-details
                  :disabled="!item.monthly_state"
                  :min="item.start"
                  variant="outlined"
                  style="min-width: 150px"
                />
              </template>

              <!-- 작업 -->
              <template #item.actions="{ item }">
                <VBtn
                  variant="outlined"
                  color="primary"
                  size="small"
                  :disabled="!ableToSave(item)"
                  @click="save(item)"
                >
                  저장
                </VBtn>
              </template>
            </VDataTable>
          </VCardText>
        </VCard>

        <VCard v-else-if="selectedClient">
          <VCardText class="text-center py-8">
            위젯 유형의 고객사는 정기 이관 설정을 할 수 없습니다.
          </VCardText>
        </VCard>

        <VCard v-else>
          <VCardText class="text-center py-8">
            고객사를 선택해주세요.
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VContainer>
</template>

<style scoped>
.bg-success-lighten {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}
</style>
